import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { PollingResult } from "./polling";
import type { PayrollStatementProjectionBuild } from "./types";

export const ProjectionBuildBanner = ({
  build,
  canCheckAgain,
  isRefreshing,
  isRetrying,
  onCheckAgain,
  onRetry,
}: {
  build: PollingResult<PayrollStatementProjectionBuild> | null;
  canCheckAgain: boolean;
  isRefreshing: boolean;
  isRetrying: boolean;
  onCheckAgain: () => void;
  onRetry: () => void;
}) => {
  if (
    !build ||
    build.status === "ready" ||
    (build.status === "pending" && !build.polling_timed_out && !canCheckAgain)
  ) {
    return null;
  }

  if (build.status === "failed") {
    return (
      <Alert color="danger">
        <XCircleIcon />
        <AlertDescription>
          <Stack direction="row" align="center" justify="between" gap={4}>
            <Stack align="start" gap={1}>
              <Text variant="caption">PayPack build failed.</Text>
              <Text tone="muted">
                {build.error ?? "The shared statement build couldn't finish."}
              </Text>
            </Stack>
            {build.retryable ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={onRetry}
                disabled={isRetrying}
                aria-busy={isRetrying || undefined}
              >
                {isRetrying && <Spinner data-icon="inline-start" aria-hidden />}
                Retry build
              </Button>
            ) : (
              <Button
                size="sm"
                variant="secondary"
                onClick={onCheckAgain}
                disabled={isRefreshing}
                aria-busy={isRefreshing || undefined}
              >
                {isRefreshing && (
                  <Spinner data-icon="inline-start" aria-hidden />
                )}
                Check again
              </Button>
            )}
          </Stack>
        </AlertDescription>
      </Alert>
    );
  }

  if (build.polling_timed_out) {
    return (
      <Alert color="warning" role="status">
        <WarningIcon />
        <AlertDescription>
          <Stack direction="row" align="center" justify="between" gap={4}>
            <Stack align="start" gap={1}>
              <Text variant="caption">
                PayPack building is taking longer than expected.
              </Text>
              <Text tone="muted">
                Check again to see if the PayPack build is ready.
              </Text>
            </Stack>
            <Button
              size="sm"
              variant="secondary"
              onClick={onCheckAgain}
              disabled={isRefreshing}
              aria-busy={isRefreshing || undefined}
            >
              {isRefreshing && <Spinner data-icon="inline-start" aria-hidden />}
              Check again
            </Button>
          </Stack>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert color="warning" role="status">
      <WarningIcon />
      <AlertDescription>
        <Stack direction="row" align="center" justify="between" gap={4}>
          <Stack align="start" gap={1}>
            <Text variant="caption">
              PayPack data could not finish loading.
            </Text>
            <Text tone="muted">
              Check again to load the completed PayPacks.
            </Text>
          </Stack>
          <Button
            size="sm"
            variant="secondary"
            onClick={onCheckAgain}
            disabled={isRefreshing}
            aria-busy={isRefreshing || undefined}
          >
            {isRefreshing && <Spinner data-icon="inline-start" aria-hidden />}
            Check again
          </Button>
        </Stack>
      </AlertDescription>
    </Alert>
  );
};
